import { createFileRoute, redirect, retainSearchParams } from '@tanstack/react-router'
import { z } from 'zod'
import { UserInfoQueries } from '@shared/data'
import { sanitizeRedirectTarget } from '@auth/redirectTarget'
import { readIdleLogoutEntry } from '@auth/data/idleLogout'
import { startSessionTransition } from '@auth/data/sessionCoordinator'
import { RESUME_LOGIN_URL } from '@auth/resume/resumeLogin'
import { isAxiosError } from 'axios'

/**
 * The BFF's `error` values for a login that took longer than its 15-minute flow cookies, or had to
 * start again.
 */
const RESTART_ERRORS = new Set(['timeout', 'restart'])

/**
 * Renders nothing: it sends a logged-out user to the identity provider, and a logged-in one to `to`.
 * A tab waiting on the Resume page whose login came back with an error goes back there instead. A
 * link to this page goes to log in like any other.
 */
export const Route = createFileRoute('/_login/login')({
  validateSearch: z.object({
    to: z.string().optional(),
    // Free text: the BFF sends other values too.
    error: z.string().optional(),
  }),
  search: {
    middlewares: [retainSearchParams(['to'])],
  },
  beforeLoad: async ({ context: { queryClient }, search }) => {
    // `to` is whatever was in the URL, so it is checked before anything navigates to it.
    const to = sanitizeRedirectTarget(search.to)

    try {
      await queryClient.ensureQueryData(UserInfoQueries.userInfo())
    } catch (error) {
      // The user endpoint's contract uses 401 for an unauthenticated request. Let every other
      // failure reach the route error handling instead of starting an SSO loop for a user whose
      // session may still be valid.
      if (!isAxiosError(error) || error.response?.status !== 401) {
        throw error
      }

      if (search.error !== undefined && readIdleLogoutEntry()) {
        // The user has just finished at the identity provider, but too slowly for the login's flow
        // cookies. Starting again at once ends the login they began; it isn't an automatic
        // forward. The provider's own session is live, so it shouldn't ask a second time.
        if (RESTART_ERRORS.has(search.error)) {
          await startSessionTransition('login')
          throw redirect({ href: RESUME_LOGIN_URL, reloadDocument: true })
        }
        throw redirect({ to: '/resume' })
      }
      await startSessionTransition('login')
      throw redirect({
        href: `/api/auth/sso/login?to=${encodeURIComponent(to)}`,
        reloadDocument: true,
      })
    }
    throw redirect({ href: to, replace: true })
  },
})
